import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Truck, Clock, Globe, ShieldAlert, Mail } from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const HIGHLIGHTS = [
  { icon: Package, label: "Discreet Packaging", desc: "All orders ship in unmarked, plain packaging" },
  { icon: Clock, label: "1–3 Business Days", desc: "Handling & dispatch after order confirmation" },
  { icon: Truck, label: "Tracked Delivery", desc: "Tracking number provided via email once shipped" },
  { icon: Globe, label: "Worldwide Options", desc: "Canada, US, and international shipping available" },
];

const SECTIONS = [
  {
    id: "processing",
    title: "Order Processing",
    body: [
      "Orders are typically processed and dispatched within 1–3 business days of payment confirmation.",
      "Orders paid by Interac e-Transfer are processed once the transfer has been received and confirmed on our end.",
      "Business days are Monday through Friday, excluding Canadian statutory holidays.",
      "You will receive an email confirmation once your order has shipped, including tracking information where available.",
    ],
  },
  {
    id: "domestic",
    title: "Canadian Shipping",
    body: [
      "We offer tracked domestic shipping to all Canadian provinces and territories.",
      "Typical transit times after dispatch:",
      { list: [
        "Major metros (Toronto, Vancouver, Montreal, Calgary, etc.): 2–5 business days",
        "Remote or rural addresses: 5–10 business days",
        "Northern territories: 7–14 business days",
      ]},
      "Shipping is calculated at fulfillment based on package weight and destination.",
    ],
  },
  {
    id: "international",
    title: "International Shipping",
    body: [
      "International orders are shipped via tracked courier or postal services depending on destination.",
      "Typical transit times after dispatch:",
      { list: [
        "United States: 4–10 business days",
        "United Kingdom & European Union: 7–14 business days",
        "Australia & New Zealand: 10–18 business days",
        "Other international destinations: 10–21 business days",
      ]},
      "The customer is solely responsible for ensuring that products may lawfully be imported into their jurisdiction. Products refused, held, or destroyed by customs will not be refunded or replaced.",
      "Any customs duties, import taxes, or handling fees imposed by the destination country are the sole responsibility of the customer.",
    ],
  },
  {
    id: "handling",
    title: "Packaging & Handling",
    body: [
      "All orders ship in plain, unmarked packaging for privacy and discretion.",
      "Peptide products are packaged in insulated, temperature-controlled materials appropriate for the shipping duration.",
      "Fragile items are protected with additional cushioning and hazard labels where required.",
    ],
  },
  {
    id: "delays",
    title: "Delays & Force Majeure",
    body: [
      "Shipping times are estimates only and are not guaranteed.",
      "EndaG1 is not responsible for delays or losses caused by:",
      { list: [
        "carriers and postal services",
        "customs and border authorities",
        "severe weather or natural events",
        "labour disruptions or strikes",
        "supply chain disruptions",
        "events beyond our reasonable control",
      ]},
      "Risk of loss and title transfer to the customer upon delivery of the package to the shipping carrier.",
    ],
  },
  {
    id: "address",
    title: "Address Accuracy",
    body: [
      "It is the customer's responsibility to provide a complete and accurate shipping address at checkout.",
      "Orders returned to us due to incorrect, incomplete, or undeliverable addresses may be:",
      { list: [
        "re-shipped at the customer's expense",
        "cancelled with a partial refund excluding shipping and handling costs",
      ]},
      "We are unable to change a shipping address once an order has been dispatched. Please review your address carefully before placing your order.",
    ],
  },
  {
    id: "lost",
    title: "Lost, Damaged, or Undelivered Packages",
    body: [
      "If your package is lost in transit or arrives damaged, please contact us within 7 days of the delivery date (or the last tracking scan for lost packages).",
      "We will work with the carrier to file a claim and resolve the issue.",
      "Claims filed outside of the 7-day window may not be eligible for resolution.",
      "In cases of confirmed carrier loss, we will either replace the order or issue store credit at our discretion.",
    ],
  },
];

function Body({ body }) {
  return body.map((b, i) => {
    if (typeof b === "string") {
      return <p key={i} className="mt-4 text-[15px] leading-[1.75] text-[#C9C9C9]">{b}</p>;
    }
    if (b.list) {
      return (
        <ul key={i} className="mt-3 mb-1 pl-5 space-y-1.5">
          {b.list.map((item, j) => (
            <li key={j} className="text-[14px] leading-[1.7] text-[#C9C9C9] list-disc marker:text-[#4ADE80]">{item}</li>
          ))}
        </ul>
      );
    }
    return null;
  });
}

export default function Shipping() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10] text-[#E5E5E5]">
      <SEO
        title="Shipping & Delivery"
        description="EndaG1 shipping policy — fast, discreet, cold-chain delivery on all research peptide orders. Domestic and international rates, transit times, and tracking."
        canonicalPath="/shipping"
      />
      <section className="mx-auto max-w-[900px] w-full px-5 sm:px-8 py-14 sm:py-20 flex-1">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8] hover:text-white transition-colors mb-8" data-testid="shipping-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#4ADE80]">Logistics</p>
          <h1 className="mt-4 font-serif-editorial text-4xl sm:text-5xl md:text-6xl text-[#F5F5F5] leading-tight">Shipping Terms</h1>
          <p className="mt-4 text-sm text-[#7a7a7a]">Last Updated: May 7, 2026</p>
        </motion.div>

        {/* Highlights */}
        <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4" data-testid="shipping-highlights">
          {HIGHLIGHTS.map((h) => (
            <div key={h.label} className="border border-white/10 bg-[#171717] p-5">
              <h.icon className="h-5 w-5 text-[#4ADE80]" strokeWidth={1.5} />
              <div className="mt-3 font-display text-sm text-[#F5F5F5]">{h.label}</div>
              <div className="text-xs text-[#A8A8A8] mt-1 leading-relaxed">{h.desc}</div>
            </div>
          ))}
        </div>

        {/* Overview */}
        <div className="mt-14 space-y-6">
          <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
            These Shipping Terms explain how EndaG1 Inc. (&ldquo;EndaG1&rdquo;) handles order processing, packaging, dispatch, delivery, and related logistics for all purchases made through our website.
          </p>
          <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
            By placing an order with EndaG1, you agree to these Shipping Terms in addition to our <Link to="/terms" className="text-[#4ADE80] hover:text-[#6FE998]">Terms of Service</Link> and <Link to="/privacy" className="text-[#4ADE80] hover:text-[#6FE998]">Privacy Statement</Link>.
          </p>
        </div>

        {/* No-returns callout */}
        <div className="mt-10 border border-[#4ADE80]/30 bg-[#4ADE80]/[0.04] p-6 flex items-start gap-4" data-testid="no-returns-callout">
          <ShieldAlert className="h-6 w-6 text-[#4ADE80] shrink-0 mt-1" strokeWidth={1.5} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#4ADE80]">All Sales Final</div>
            <p className="mt-2 text-[15px] leading-[1.7] text-[#F5F5F5]">Due to the sensitive nature of our research products, we do not accept returns or exchanges. Damaged or incorrect orders must be reported within 7 days of delivery.</p>
          </div>
        </div>

        {/* TOC */}
        <nav aria-label="Table of contents" data-testid="shipping-toc" className="mt-14 border border-white/10 bg-[#171717] p-6 sm:p-7">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4ADE80] mb-3">Sections</div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-[#C9C9C9] hover:text-[#4ADE80] transition-colors">
                  <span className="text-[#7a7a7a] font-mono mr-2">{String(i + 1).padStart(2, "0")}.</span>
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        <div className="mt-16 space-y-16">
          {SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} data-testid={`shipping-section-${s.id}`} className="scroll-mt-24">
              <div className="flex items-baseline gap-3">
                <span className="text-[11px] font-mono text-[#4ADE80]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-2xl sm:text-3xl text-[#F5F5F5] tracking-tight">{s.title}</h2>
              </div>
              <div className="mt-3 pl-9">
                <Body body={s.body} />
              </div>
            </section>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-16 pt-10 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4ADE80] mb-4">Questions About Your Shipment?</div>
          <div className="border border-white/10 bg-[#171717] p-6 max-w-md">
            <div className="font-display text-lg text-[#F5F5F5]">EndaG1 Inc.</div>
            <Link to="/#contact" data-testid="shipping-contact-form-link" className="mt-4 inline-flex items-center gap-2 bg-[#4ADE80] text-[#0F0F10] px-5 py-2.5 uppercase tracking-[0.14em] text-[11px] hover:bg-[#6FE998] transition-colors">
              <Mail className="h-4 w-4" /> Contact Us
            </Link>
            <a href="mailto:info@endag1.com" className="mt-3 block text-sm text-[#7a7a7a] hover:text-[#4ADE80] transition-colors">info@endag1.com</a>
          </div>
        </div>
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
